'use client'

import { useEffect, useRef } from 'react'
import Script from 'next/script'

const TRUSTPILOT_BUSINESS_UNIT_ID = '54a40c7700006400057c6e53'
const TRUSTPILOT_TEMPLATE_ID = '5613c9cde69ddc09340c6beb'
const TRUSTPILOT_PROFILE_URL = 'https://www.trustpilot.com/review/www.wanderu.com'
const TRUSTPILOT_SCRIPT_URL =
    'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'

interface TrustpilotWidgetProps {
    locale: string
}

/**
 * Formats a nexus locale (e.g. "en-us") to the format Trustpilot expects (e.g. "en-US").
 */
function formatTrustpilotLocale(locale: string): string {
    const [lang, region] = locale.split('-')
    return region ? `${lang}-${region.toUpperCase()}` : locale
}

/**
 * TrustpilotWidget renders the real Trustpilot review widget using their SDK.
 * Falls back to a link to the Trustpilot profile when the script hasn't loaded.
 */
export function TrustpilotWidget({ locale }: TrustpilotWidgetProps) {
    const widgetRef = useRef<HTMLDivElement>(null)

    const loadWidget = () => {
        const tp = (window as unknown as Record<string, unknown>).Trustpilot as
            | { loadFromElement: (el: HTMLElement, useNewApi: boolean) => void }
            | undefined
        if (tp?.loadFromElement && widgetRef.current) {
            tp.loadFromElement(widgetRef.current, true)
        }
    }

    useEffect(() => {
        loadWidget()
    }, [])

    return (
        <section
            className="bg-[#e6f9fb] py-6 md:py-8"
            data-section="trustpilot"
            id="trustpilot-reviews-section"
        >
            <div className="mx-auto max-w-[1056px] px-4">
                <h2 className="mb-6 text-center text-2xl leading-[30px] font-bold text-[#2e3033] md:mb-8">
                    See what travelers are saying about Wanderu:
                </h2>
                <div
                    ref={widgetRef}
                    className="trustpilot-widget"
                    data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
                    data-template-id={TRUSTPILOT_TEMPLATE_ID}
                    data-theme="light"
                    data-locale={formatTrustpilotLocale(locale)}
                    data-style-height="100%"
                    data-style-width="100%"
                >
                    <a href={TRUSTPILOT_PROFILE_URL} target="_blank" rel="noopener noreferrer">
                        Trustpilot
                    </a>
                </div>
            </div>
            <Script src={TRUSTPILOT_SCRIPT_URL} strategy="afterInteractive" onLoad={loadWidget} />
        </section>
    )
}
